import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import LeadStreamsService from 'src/services/LeadStreamsService';
import { getStreamStatus } from 'src/utils/leadStreams';
import LeadDemandPreview from './LeadDemandPreview';

import type { Stream } from 'src/services/LeadStreamsService/agent';
import type { ILeadExample } from 'src/services/NicheAnalysisService';
import type { LeadExampleChoiceValue } from 'src/utils/leadStreamFilterProfile';

type Decisions = Record<number, LeadExampleChoiceValue | null>;
interface Props {
    categoryId?: number;
    examples: ILeadExample[];
    choices: Record<number, LeadExampleChoiceValue>;
    onChoose: (id: number, choice: LeadExampleChoiceValue | null) => void;
    onHydrate: (decisions: Decisions) => void;
    onLoadMore?: () => Promise<void>;
}

export function savedExampleDecisions(stream: Stream, examples: ILeadExample[]): Decisions {
    const decisions: Decisions = Object.fromEntries(examples.map((example) => [example.id, null]));
    const profile = stream.filter_profile;
    ([['positive_examples', 'fit'], ['negative_examples', 'skip'], ['maybe_examples', 'maybe']] as const)
        .forEach(([key, choice]) => (profile?.[key] || []).forEach((example) => {
            const id = Number(example.message_id ?? example.id);
            if (id in decisions) decisions[id] = choice;
        }));
    return decisions;
}

export default function ExistingStreamExamples(props: Props) {
    const [streams, setStreams] = useState<Stream[]>([]);
    const [targetId, setTargetId] = useState(0);
    const [stream, setStream] = useState<Stream | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);
    const callbacks = useRef(props);
    callbacks.current = props;
    const inFlight = useRef(false);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    useEffect(() => {
        let live = true;
        const controller = new AbortController();
        setLoading(true);
        setError('');
        LeadStreamsService.getStreams({ signal: controller.signal }).then((items) => {
            if (!live) return;
            const eligible = items.filter((item) => item.category.id === props.categoryId && getStreamStatus(item).key !== 'pending');
            setStreams(eligible);
            setTargetId(eligible.length === 1 ? eligible[0].id : 0);
            setLoading(false);
        }).catch(() => {
            if (live) { setError('Не удалось загрузить потоки. Оценки пока не применяются к запущенному потоку.'); setLoading(false); }
        });
        return () => { live = false; controller.abort(); };
    }, [props.categoryId, retry]);

    useEffect(() => {
        let live = true;
        setStream(null);
        setSaved(false);
        if (!targetId) return;
        setLoading(true);
        setError('');
        LeadStreamsService.getStream(targetId).then((current) => {
            if (!live) return;
            setStream(current);
            setLoading(false);
        }).catch(() => {
            if (live) { setError('Не удалось загрузить правила потока. Повторите загрузку.'); setLoading(false); }
        });
        return () => { live = false; };
    }, [targetId, retry]);

    const exampleIds = props.examples.map((example) => example.id).join(',');
    useEffect(() => {
        if (stream) callbacks.current.onHydrate(savedExampleDecisions(stream, callbacks.current.examples));
    }, [stream, exampleIds]);

    const choose = async (id: number, choice: LeadExampleChoiceValue | null) => {
        if (!targetId) { props.onChoose(id, choice); return; }
        if (!stream || inFlight.current) return;
        const example = props.examples.find((item) => item.id === id);
        if (!example) return;
        inFlight.current = true;
        setSaving(true);
        setSaved(false);
        setError('');
        try {
            const updated = await LeadStreamsService.trainStream(stream.id, stream.updated_at, [{
                id, text: example.text.trim().replace(/\s+/g, ' ').slice(0, 260), choice,
            }]);
            if (!alive.current) return;
            setStream(updated);
            props.onChoose(id, choice);
            setSaved(true);
        } catch (reason: any) {
            if (alive.current) setError(reason?.response?.status === 409
                ? 'Правила потока изменились в другой вкладке. Обновите правила и поставьте оценку ещё раз.'
                : 'Оценка не сохранена. Повторите нажатие или обновите правила потока.');
        } finally {
            inFlight.current = false;
            if (alive.current) setSaving(false);
        }
    };
    const controls = <Box sx={{ mb: 1.5 }}>
        {streams.length > 0 && <TextField
            select fullWidth size="small" label="Применять оценки" value={targetId}
            disabled={loading || saving} SelectProps={{ native: true }}
            onChange={(event) => setTargetId(Number(event.target.value))}
        >
            <option value={0}>Только к новому потоку</option>
            {streams.map((item) => <option key={item.id} value={item.id}>{item.display_name || item.name || item.category.name}</option>)}
        </TextField>}
        <Typography role="status" aria-live="polite" sx={{ mt: 0.75, color: 'var(--lm-muted)', fontSize: 13 }}>
            {saving ? 'Сохраняем оценку…' : loading ? 'Проверяем потоки…' : targetId
                ? saved ? 'Сохранено. Агент учтёт оценки для новых лидов.' : 'Отметки автоматически сохраняются в выбранный поток.'
                : 'Оценки будут применены при запуске нового потока.'}
        </Typography>
        {error && <Alert severity="warning" sx={{ mt: 1 }} action={<Button disabled={saving} onClick={() => setRetry((value) => value + 1)}>Обновить</Button>}>{error}</Alert>}
    </Box>;
    return <LeadDemandPreview examples={props.examples} choices={props.choices}
        onChoose={(id, choice) => void choose(id, choice)} onLoadMore={props.onLoadMore}
        collapsibleAfterReview trainingControls={controls} choicesDisabled={loading || saving || Boolean(targetId && !stream)} />;
}
